import React, { useEffect, useMemo } from "react";
import { View } from "react-native";

import Image from "$src/core/components/atoms/Image/Image";
import SnapPickerItem from "$src/core/components/atoms/SnapPickerItem/SnapPickerItem";
import Text from "$src/core/components/atoms/Text/Text";
import HorizontalSnapScrollView from "$src/core/components/molecules/HorizontalSnapScrollView/HorizontalSnapScrollView";
import useHorizontalSnapPickerMetrics from "$src/core/hooks/use-horizontal-snap-picker-metrics";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import chunkChannelsToGroups from "$src/core/utils/epg/chunk-channels-to-groups";
import EPG_TV_CHANNELS_PER_PAGE from "$src/core/utils/epg/epg-tv-channels-per-page";
import getEpgChannelLogoUri from "$src/core/utils/epg/get-epg-channel-logo-uri";
import getTvChannelGroupIndex from "$src/core/utils/epg/get-tv-channel-group-index";

import $, { IMAGE_DIMENSIONS } from "./EPGChannelSelector.styles";
import type {
  EPGChannelSelectorChannel,
  EPGChannelSelectorProps,
} from "./types";

type EPGChannelSelectorLogoProps = {
  channel: EPGChannelSelectorChannel;
  isActive: boolean;
};

const EPGChannelSelectorLogo = ({
  channel,
  isActive,
}: EPGChannelSelectorLogoProps) => {
  const logoUri = getEpgChannelLogoUri({
    logo: channel.logo,
    logoDark: channel.logoDark,
    isActive,
  });

  const source = useResponsiveImage(logoUri, {
    dimensions: IMAGE_DIMENSIONS.logo,
  });

  if (!source) {
    return (
      <Text
        accessible={false}
        style={[$.title, isActive && $.titleActive]}
        preset="tiny"
        ellipsizeMode="tail"
        numberOfLines={1}
        accessibilityRole="text"
      >
        {channel.title}
      </Text>
    );
  }

  return (
    <Image
      accessible={false}
      accessibilityRole="image"
      accessibilityLabel={channel.title}
      source={source}
      style={$.logo}
      contentFit="contain"
    />
  );
};

/** Desktop: one pill per group of three channels, matching the grid columns. */
const EPGChannelSelector: React.FC<EPGChannelSelectorProps> = ({
  channels,
  selectedChannelId,
  onChannelSelect,
  selectedItemRef,
  onItemFocus,
}) => {
  const groups = useMemo(
    () => chunkChannelsToGroups(channels, EPG_TV_CHANNELS_PER_PAGE),
    [channels],
  );

  const { scrollViewRef, onLayout, itemWidth, snapToInterval, centerItem } =
    useHorizontalSnapPickerMetrics({
      // A group pill holds three logos, so it needs more room than a label.
      minItemWidthStep: 60,
      totalItems: groups.length,
    });

  const selectedIndex = channels.findIndex((c) => c.id === selectedChannelId);
  const selectedGroupIndex =
    selectedIndex >= 0
      ? getTvChannelGroupIndex(selectedIndex, EPG_TV_CHANNELS_PER_PAGE)
      : 0;

  /* Keep the selected group centred, same as the date picker above it. */
  useEffect(() => {
    centerItem(selectedGroupIndex, groups.length);
  }, [centerItem, groups.length, selectedGroupIndex]);

  const handleSelect = (
    group: EPGChannelSelectorChannel[],
    groupIndex: number,
  ) => {
    onChannelSelect?.(group[0].id, groupIndex * EPG_TV_CHANNELS_PER_PAGE);
    centerItem(groupIndex, groups.length);
  };

  if (!channels.length) {
    return null;
  }

  return (
    <HorizontalSnapScrollView
      ref={scrollViewRef}
      onLayout={onLayout}
      snapToInterval={snapToInterval}
    >
      {groups.map((group, groupIndex) => {
        const isActive = group.some((c) => c.id === selectedChannelId);

        return (
          <SnapPickerItem
            key={group.map((c) => c.id).join("-")}
            ref={isActive ? selectedItemRef : undefined}
            width={itemWidth}
            isActive={isActive}
            accessibilityLabel={group.map((c) => c.title).join(", ")}
            accessibilityState={{ selected: isActive }}
            styleExtension={$.itemFlex}
            onItemFocus={onItemFocus}
            onPress={() => handleSelect(group, groupIndex)}
          >
            <View style={$.content}>
              {group.map((channel) => (
                <EPGChannelSelectorLogo
                  key={channel.id}
                  channel={channel}
                  isActive={isActive}
                />
              ))}
            </View>
          </SnapPickerItem>
        );
      })}
    </HorizontalSnapScrollView>
  );
};

export default EPGChannelSelector;
